import { useMutation } from "@tanstack/react-query";
import {
  fileUploadService,
  UploadProgressEvent,
} from "@/services/fileUploadService";
import { useScopeParams } from "./useScopeParams";

export interface FileUploadParams {
  file: File;
  uid: string;
  onProgress?: (uid: string, percent: number) => void;
}

export interface UseFileUploadMutationOptions {
  onSuccess?: (uid: string, fileName: string) => void;
  onError?: (uid: string, fileName: string, error: Error) => void;
}

export const useFileUploadMutation = (
  options?: UseFileUploadMutationOptions,
) => {
  const { scope, id } = useScopeParams();

  const uploadMutation = useMutation({
    mutationFn: async ({ file, uid, onProgress }: FileUploadParams) => {
      if (!id) {
        throw new Error("Trial ID is required for file upload");
      }

      const handleProgress = (event: UploadProgressEvent) => {
        onProgress?.(uid, event.percent);
      };

      const result = await fileUploadService.uploadFile(
        scope,
        id,
        file,
        handleProgress,
      );
      return { result, uid, fileName: file.name };
    },
    onSuccess: ({ uid, fileName }) => {
      options?.onSuccess?.(uid, fileName);
    },
    onError: (error: Error, variables) => {
      options?.onError?.(variables.uid, variables.file.name, error);
    },
  });

  return {
    uploadFile: uploadMutation.mutate,
    uploadFileAsync: uploadMutation.mutateAsync,
    isUploading: uploadMutation.isPending,
  };
};
